import React from 'react';
import clsx from 'clsx';
import { capitalize } from 'string-transform';

import Infotip from 'App/common/Tooltip/Infotip/infotip';
import AddressDisplay from 'App/DetailsPage/common/AddressDisplay/AddressDisplay';
import { CommonClass } from 'config/track-config';
import { CustomerAuthLevel } from 'models/Customer';
import { ArticleAddress } from 'models/shipments';
import { selectAuthLevel } from 'store/customer';
import { useAppSelector } from 'store/store';
import { formatAddress } from 'utils/string.utils';

import { useCurrentArticle, useFirstArticle } from '../../hooks/shipment.hooks';
import {
  ADDRESS_LINES_TEST_ID,
  ADDRESS_TEST_ID,
  RECIPIENT_NAME_TEST_ID,
  REDIRECT_INFO_TEST_ID,
} from '../DetailsPanel.test-ids';
import InfoRow from '../InfoRow/InfoRow';
import RedirectInfo from './RedirectInfo/RedirectInfo';

import detailsPanelStyles from '../DetailsPanel.module.scss';
import styles from './DeliveryAddress.module.scss';

function transformAddressInfo(
  userOwnership: boolean | undefined,
  authLevel: CustomerAuthLevel,
  address?: ArticleAddress | null
): {
  addressLines: string[];
  displayNoAddressLines?: JSX.Element;
  recipientNameDisplay?: string | null | JSX.Element;
} {
  if (authLevel === CustomerAuthLevel.ANONYMOUS) {
    return { displayNoAddressLines: <span>Can&apos;t be shown</span>, addressLines: [] };
  }

  if (!userOwnership)
    return {
      displayNoAddressLines: noOwnership,
      addressLines: [],
    };

  if (!address) return { displayNoAddressLines: <span>Not available</span>, addressLines: [] };

  const addressLines = formatAddress(address);
  const recipientName = capitalize(address.addressee?.toLowerCase() ?? '') || noRecipientName;

  if (!addressLines.length)
    return {
      displayNoAddressLines: noAddressLines,
      recipientNameDisplay: capitalize(address.addressee?.toLowerCase() ?? ''),
      addressLines: [],
    };

  return { addressLines, recipientNameDisplay: recipientName };
}

const DeliveryAddress: React.FC = () => {
  const address = useCurrentArticle()?.address;
  const userOwnership = useFirstArticle()?.userOwnership || false;
  const authLevel = useAppSelector(selectAuthLevel);

  const { addressLines, recipientNameDisplay, displayNoAddressLines } = transformAddressInfo(
    userOwnership,
    authLevel,
    address
  );

  return (
    <>
      <InfoRow heading='Delivery address'>
        <span className={styles.textContent} data-testid={ADDRESS_TEST_ID}>
          <AddressDisplay
            className={CommonClass.FULLSTORY_MASK}
            addressLines={addressLines}
            data-testid={ADDRESS_LINES_TEST_ID}
          />
          {displayNoAddressLines}
        </span>
        {userOwnership && (
          <div data-testid={REDIRECT_INFO_TEST_ID}>
            <RedirectInfo />
          </div>
        )}
      </InfoRow>
      <div className={detailsPanelStyles.divider} />
      {recipientNameDisplay && (
        <InfoRow heading="Recipient's name">
          <div className={clsx(styles.textContent, CommonClass.FULLSTORY_MASK)} data-testid={RECIPIENT_NAME_TEST_ID}>
            {recipientNameDisplay}
          </div>
          <div className={detailsPanelStyles.divider} />
        </InfoRow>
      )}
    </>
  );
};

const noAddressLines = (
  <div className={styles.row}>
    <span>Not available</span>
    <Infotip floatClassName={styles.addressInfotip}>
      <p>The sender hasn&apos;t given us all the delivery details for this parcel.</p>
      <p> Don&apos;t worry, though. It&apos;ll be on the parcel – either hand-written or printed on the label.</p>
    </Infotip>
  </div>
);

const noRecipientName = (
  <div className={styles.row}>
    <span>Not available</span>
    <Infotip>
      <p>
        The sender hasn&apos;t given us all the delivery details for this parcel, so we can&apos;t show the name here.
      </p>
    </Infotip>
  </div>
);

const noOwnership = (
  <div className={styles.row}>
    <span>Can&apos;t be shown</span>
    <Infotip>
      <p>
        The delivery details for this parcel don&apos;t match your MyPost account details, so we can&apos;t show the
        delivery address here.
      </p>
    </Infotip>
  </div>
);

export default DeliveryAddress;
